import { ScheduleFailureCategory } from "@/enum/schedule.enum";

import { ListPerson } from "../../../../../schema/list";
import { ListPersonSchedule } from "../../../../../schema/list-person-schedule.schema";

/**
 * The LinkedIn invite-acceptance gate. It never sends anything — it just watches for acceptance
 * for 30 days — so a failure recorded on it is not a failed *send* and must not be surfaced as one.
 * Kept in lockstep with the same exclusion in `outbox-category.helper.ts`.
 */
const INVITE_ACCEPTANCE_GATE_STEP_ID = "n99-cond";

/**
 * Categories that mean something on the sender's side went wrong and the user (or we) must act.
 * Mirrors the API's `NEEDS_ATTENTION_CATEGORIES` in `support-send-errors.util.ts`, plus the two
 * self-inflicted content problems (an over-long text, a column without a value) and an empty credit
 * balance. Everything else is a recipient- or
 * data-side condition that is expected for some prospects, so it reads as a warning rather than an
 * error.
 */
const NEEDS_ATTENTION_CATEGORIES: ReadonlySet<ScheduleFailureCategory> = new Set([
	ScheduleFailureCategory.DISCONNECTED,
	ScheduleFailureCategory.RATE_LIMITED,
	ScheduleFailureCategory.INSUFFICIENT_PERMISSIONS,
	ScheduleFailureCategory.TRANSIENT,
	// The step may have gone out: the user has to check before sending it again (PREC-4160).
	ScheduleFailureCategory.TIMED_OUT,
	ScheduleFailureCategory.UNKNOWN,
	ScheduleFailureCategory.CONTENT_TOO_LONG,
	ScheduleFailureCategory.NO_INMAIL_CREDITS,
	ScheduleFailureCategory.NOT_ENOUGH_CREDITS,
	// The user fills in the value or edits the step, then sends it again (PREC-4440).
	ScheduleFailureCategory.MISSING_COLUMN_VALUE,
	// The campaign ended for this lead at the invite step; the user reaches out by hand (PREC-4426).
	ScheduleFailureCategory.ALREADY_CONNECTED,
]);

export interface StepFailure {
	/** The schedule that failed — the caller uses its id/stepId to match the campaign-overview node. */
	scheduleId: number;
	stepId: string | null;
	stepType: string | null;
	category: ScheduleFailureCategory;
	failedAt: string;
	severity: "error" | "warning";
	/** For a send the blacklist blocked: the entry that matched, so the user knows what to remove. */
	blockedBy?: ListPersonSchedule["blockedBy"] | null;
	/** For a step not sent because a column had no value: the columns, by name (PREC-4440). */
	missingColumns?: string[] | null;
}

/** Whether this schedule represents a real, still-relevant failed send. */
const isFailedSend = (schedule: ListPersonSchedule): boolean =>
	!!schedule.failedAt && !schedule.canceledAt && schedule.stepId !== INVITE_ACCEPTANCE_GATE_STEP_ID;

export const getFailureSeverity = (category: ScheduleFailureCategory): "error" | "warning" =>
	NEEDS_ATTENTION_CATEGORIES.has(category) ? "error" : "warning";

/**
 * The lead's most recent failed step, or null when nothing failed.
 *
 * "Most recent" is by `failedAt`, so a lead whose later steps ran fine still reports the newest
 * failure rather than the first one. A step that was canceled afterwards is ignored — the send was
 * called off, so the failure is no longer something the user can act on.
 */
export const resolveStepFailure = (row: ListPerson): StepFailure | null => {
	const failed = (row.schedules ?? [])
		.filter(isFailedSend)
		.sort((a, b) => new Date(b.failedAt!).getTime() - new Date(a.failedAt!).getTime())[0];

	if (!failed) {
		return null;
	}

	const category = failed.failureCategory ?? ScheduleFailureCategory.UNKNOWN;

	return {
		scheduleId: failed.id,
		stepId: failed.stepId,
		stepType: failed.stepType ?? null,
		category,
		failedAt: failed.failedAt!,
		severity: getFailureSeverity(category),
		blockedBy: failed.blockedBy ?? null,
		missingColumns: failed.missingColumns ?? null,
	};
};
